import { apiUrl, redirectOnDenied, webAuth } from '../auth/web-auth.js';

/**
 * Cliente da área do cliente (`/v1/client/**`, WA01).
 *
 * Somente leitura, sempre com o Bearer da sessão central. O servidor aplica o
 * escopo autorizado em toda consulta; recusa de acesso (401/403) redireciona
 * para login ou para a tela de estado e nunca vira dado na tela.
 */

export interface DemoDirectoryItem {
  readonly id: string;
  readonly name: string;
  readonly tenantId?: string;
}

export interface DemoSession {
  readonly sessionId: string;
  readonly farmId: string;
  readonly captureMode: string;
  readonly dataContext: string;
  readonly status: 'open' | 'incomplete' | 'conflicted' | 'complete';
  readonly startedAtMs: number;
  readonly firstReceivedAtMs: number;
  readonly storedRecordCount: number;
  readonly recordsByType: Record<string, number>;
  readonly clockWarning: boolean;
}

export interface DemoObservation {
  readonly recordId: string;
  readonly sequence: number;
  readonly epcRaw: string;
  readonly epcNormalized: string | null;
  readonly epcInvalidReason: string | null;
  readonly occurredAtMs: number;
  readonly receivedAtMs: number;
  readonly peakRssiDbm: number | null;
  readonly seenCount: number;
  readonly associationState: string;
  readonly animalId: string | null;
  readonly poleId: string | null;
  readonly tagId: string | null;
  readonly bindingId: string | null;
  readonly clockWarning: boolean;
  readonly farmEligibility: string;
}

export interface PublishedDay {
  readonly date: string;
  readonly asOfMs: number;
}

/** Hoje: agora do servidor e a data de São Paulo desse instante (não é dia publicado). */
export type CurrentReference = PublishedDay;

export interface ReadingRound {
  readonly sessionId: string;
  readonly date: string;
  readonly startedAtMs: number;
  readonly endedAtMs: number | null;
  readonly tagCount: number;
  readonly state: 'completed' | 'processing' | 'incomplete';
}

/** Acesso negado e já redirecionado: a tela não mostra dado nem erro técnico. */
export class AccessDeniedError extends Error {
  constructor() {
    super('Acesso indisponível.');
    this.name = 'AccessDeniedError';
  }
}

/** Cliente/fazenda fora do escopo ou inexistente (o servidor não distingue). */
export class NotFoundError extends Error {
  constructor() {
    super('Recurso não encontrado.');
    this.name = 'NotFoundError';
  }
}

async function request(path: string, headers: Record<string, string> = {}): Promise<Response> {
  const response = await webAuth().authFetch(apiUrl(`/v1${path}`), { headers: { accept: 'application/json', ...headers } });
  if (await redirectOnDenied(response)) throw new AccessDeniedError();
  if (response.status === 404) throw new NotFoundError();
  if (!response.ok && response.status !== 304) throw new Error(`Falha ao consultar (HTTP ${response.status}).`);
  return response;
}

async function read<T>(path: string): Promise<T> {
  return (await (await request(path)).json()) as T;
}

export const demoApi = {
  tenants: () => read<{ items: DemoDirectoryItem[] }>('/client/tenants'),
  farms: (tenantId: string) =>
    read<{ items: DemoDirectoryItem[] }>(
      `/client/tenants/${encodeURIComponent(tenantId)}/farms`,
    ),
  sessions: (tenantId: string, farmId: string | null, cursor?: string) => {
    const params = new URLSearchParams({ tenantId });
    if (farmId !== null) params.set('farmId', farmId);
    if (cursor !== undefined) params.set('cursor', cursor);
    return read<{ items: DemoSession[]; nextCursor: string | null; knowledgeRevision: string }>(
      `/client/capture/sessions?${params.toString()}`,
    );
  },
  session: (sessionId: string) =>
    read<{
      sessionId: string;
      receipt: { status: string; cleanupAuthorized: boolean; missingSequenceRanges: unknown[] };
      counts: {
        observationRecordCount: number;
        detectionTotal: number;
        byAssociationState: Record<string, number>;
      };
    }>(`/client/capture/sessions/${encodeURIComponent(sessionId)}`),
  /**
   * Hoje (`current`, o agora do servidor) e os dias com posições publicadas
   * (`asOfMs` = fim do dia em São Paulo). Tudo pronto do servidor.
   */
  /** Só Hoje (`limit: 0`, sem consulta histórica) ou uma página de dias anteriores a `before`. */
  publishedDays: (tenantId: string, farmId: string, page: { limit: number; before?: string }) => {
    const params = new URLSearchParams({ tenantId, farmId, limit: String(page.limit) });
    if (page.before !== undefined) params.set('before', page.before);
    return read<{ current: CurrentReference; days: PublishedDay[]; nextBefore: string | null }>(
      `/client/published-days?${params.toString()}`,
    );
  },
  /** Geometria da fazenda vigente no instante (Hoje ou fim de um dia publicado). */
  mapContext: (tenantId: string, farmId: string, asOfMs: number) =>
    read<MapContext>(`/client/map-context?${new URLSearchParams({ tenantId, farmId, asOfMs: String(asOfMs) }).toString()}`),
  readingRounds: (tenantId: string, farmId: string, cursor?: string) => {
    const params = new URLSearchParams({ tenantId, farmId });
    if (cursor !== undefined) params.set('cursor', cursor);
    return read<{ items: ReadingRound[]; nextCursor: string | null }>(
      `/client/reading-rounds?${params.toString()}`,
    );
  },
  observations: (sessionId: string, cursor?: string) => {
    const params = new URLSearchParams();
    if (cursor !== undefined) params.set('cursor', cursor);
    const query = params.toString();
    return read<{ items: DemoObservation[]; nextCursor: string | null }>(
      `/client/capture/sessions/${encodeURIComponent(sessionId)}/observations${query === '' ? '' : `?${query}`}`,
    );
  },
};

/** Rótulo do estado de associação, em português, sem inventar categoria nova. */
export function associationLabel(state: string): string {
  switch (state) {
    case 'bound_animal':
      return 'animal vinculado';
    case 'bound_pole':
      return 'poste vinculado';
    case 'known_unbound':
      return 'etiqueta cadastrada, sem vínculo';
    case 'unknown_epc':
      return 'EPC desconhecido';
    case 'invalid_epc':
      return 'EPC inválido';
    case 'foreign_tenant':
      return 'etiqueta de outro cliente';
    default:
      return state;
  }
}

export function sessionStatusLabel(status: string): string {
  switch (status) {
    case 'open':
      return 'aberta';
    case 'incomplete':
      return 'incompleta';
    case 'conflicted':
      return 'com conflito';
    case 'complete':
      return 'completa';
    default:
      return status;
  }
}

/* ------------------------------------------------------------------ *
 * M03 — posições estimadas, contexto do mapa e indicadores
 * ------------------------------------------------------------------ */

export type PositionState = 'valid' | 'last_known' | 'unlocated' | 'never_estimated';

export interface EstimateItem {
  readonly targetKey: string;
  readonly targetKind: 'bound_animal' | 'known_tag_without_binding';
  readonly animalId: string | null;
  readonly tagId: string;
  readonly bindingId: string | null;
  readonly epc: string;
  readonly counted: boolean;
  readonly registeredFarmId: string | null;
  readonly captureFarmId: string | null;
  readonly expectedPastureId: string | null;
  readonly expectedPastureName: string | null;
  readonly lastReadAtMs: number | null;
  readonly positionAtMs: number | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly uncertaintyMeters: number | null;
  readonly representativeOnly: boolean;
  readonly classification: string;
  readonly pastureId: string | null;
  readonly pastureName: string | null;
  readonly positionState: PositionState;
  readonly positionReason: string | null;
  readonly alerts: readonly string[];
  readonly algorithmVersion: string | null;
  readonly provenance: {
    readonly processingRunId: string;
    readonly sessionId: string;
    readonly inputRevision: string;
    readonly evidenceBoundaryId: string;
    readonly publishedAtMs: number;
  } | null;
}

export interface EstimatePage {
  readonly resolvedAsOfMs: number;
  readonly countedSubject: 'active_cattle_tags';
  readonly indicators: {
    readonly total: number;
    readonly valid: number;
    readonly lastKnown: number;
    readonly unlocated: number;
    readonly neverEstimated: number;
    readonly neverRead: number;
    readonly ambiguous: number;
  };
  readonly readAgeBands: readonly { fromHoursInclusive: number; toHoursExclusive: number; tagCount: number }[];
  readonly neverReadTagCount: number;
  readonly items: readonly EstimateItem[];
  readonly nextCursor: string | null;
}

export interface AnimalListItem {
  readonly targetKey: string;
  readonly epc: string;
  readonly positionState: PositionState;
  readonly approximate: boolean;
  readonly pastureName: string | null;
  readonly expectedPastureName: string | null;
  readonly lastReadAtMs: number | null;
}

export interface MapContext {
  readonly farmName: string | null;
  readonly resolvedAsOfMs: number;
  readonly pastures: readonly {
    readonly pastureId: string;
    readonly name: string;
    readonly vertices: readonly { poleId: string; ordinal: number; latitude: number; longitude: number }[];
  }[];
  readonly poles: readonly { poleId: string; epc: string | null; latitude: number; longitude: number; origin: string }[];
}

/**
 * Metadados completos de uma fazenda num instante: todas as páginas do MESMO
 * `resolvedAsOfMs` (o cursor o congela). Uma requisição por página, nunca uma
 * por marcador. Fora do caminho crítico do mapa (M03-FP): só enriquece
 * tooltip, detalhe e lista.
 */
export async function loadEstimateItems(
  tenantId: string,
  farmId: string,
  asOfMs: number,
): Promise<{ page: EstimatePage; items: EstimateItem[] }> {
  const items: EstimateItem[] = [];
  let first: EstimatePage | null = null;
  let cursor: string | null = null;
  do {
    const params = new URLSearchParams({ tenantId, farmId, pageSize: '500', asOfMs: String(asOfMs) });
    if (cursor !== null) params.set('cursor', cursor);
    const page: EstimatePage = await read<EstimatePage>(`/client/estimates?${params.toString()}`);
    first ??= page;
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return { page: first, items };
}

/** Lista leve do modal; o detalhe analítico permanece sob demanda por animal. */
export async function loadAnimalList(tenantId: string, farmId: string, asOfMs: number): Promise<AnimalListItem[]> {
  const params = new URLSearchParams({ tenantId, farmId, asOfMs: String(asOfMs) });
  return (await read<{ items: AnimalListItem[] }>(`/client/animals?${params.toString()}`)).items;
}

export function animalListStatus(item: AnimalListItem, asOfMs: number): SimpleStatus {
  if (item.lastReadAtMs === null || asOfMs - item.lastReadAtMs >= DAY_MS) return 'no_recent_read';
  if (item.positionState !== 'valid' && item.positionState !== 'last_known') return 'unlocated';
  return item.approximate ? 'approximate' : 'located';
}

export async function loadEstimateItem(
  tenantId: string, farmId: string, asOfMs: number, targetKey: string,
): Promise<EstimateItem> {
  const params = new URLSearchParams({ tenantId, farmId, asOfMs: String(asOfMs), targetKey });
  return read<EstimateItem>(`/client/estimate-detail?${params.toString()}`);
}

/* ------------------------------------------------------------------ *
 * M03-FP — marcadores mínimos do mapa
 * ------------------------------------------------------------------ */

/** Ordem fixa das tuplas de `rfid.mvp.map-marker-snapshot/1.0.0`. */
export const MARKER_COLUMNS = [
  'targetKey', 'latitude', 'longitude', 'lastReadAtMs', 'positionState', 'captureFarmId', 'counted',
] as const;

/** O mínimo que o mapa e os quatro números precisam; nomes e pastos vêm dos metadados. */
export interface MapMarker {
  readonly targetKey: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly lastReadAtMs: number | null;
  readonly positionState: PositionState;
  readonly captureFarmId: string | null;
  readonly counted: boolean;
}

type MarkerTuple = [string, number | null, number | null, number | null, PositionState, string | null, boolean];

export interface MarkerSnapshot {
  readonly resolvedAsOfMs: number;
  readonly markers: readonly MapMarker[];
  readonly etag: string | null;
}

/**
 * Marcadores do instante, revalidados pelo ETag guardado: `null` quando o
 * servidor responde 304 (o que está na tela continua valendo). Colunas fora
 * da ordem do contrato são recusadas, não reinterpretadas.
 */
export async function loadMapMarkers(
  tenantId: string,
  farmId: string,
  asOfMs: number,
  etag: string | null,
): Promise<MarkerSnapshot | null> {
  const params = new URLSearchParams({ tenantId, farmId, asOfMs: String(asOfMs) });
  const response = await request(`/client/map-markers?${params.toString()}`, etag === null ? {} : { 'if-none-match': etag });
  if (response.status === 304) return null;
  const body = (await response.json()) as { resolvedAsOfMs: number; columns: string[]; markers: MarkerTuple[] };
  if (body.columns.join() !== MARKER_COLUMNS.join()) throw new Error('Formato de marcadores inesperado.');
  return {
    resolvedAsOfMs: body.resolvedAsOfMs,
    etag: response.headers.get('etag'),
    markers: body.markers.map(([targetKey, latitude, longitude, lastReadAtMs, positionState, captureFarmId, counted]) => ({
      targetKey, latitude, longitude, lastReadAtMs, positionState, captureFarmId, counted,
    })),
  };
}

export function positionStateLabel(state: PositionState): string {
  switch (state) {
    case 'valid':
      return 'posição estimada';
    case 'last_known':
      return 'posição antiga';
    case 'unlocated':
      return 'lida, sem posição';
    case 'never_estimated':
      return 'sem estimativa';
  }
}

export function alertLabel(alert: string): string {
  switch (alert) {
    case 'unbound_tag':
      return 'tag sem vínculo com animal';
    case 'retired_tag':
      return 'tag com baixa';
    case 'registered_in_other_farm':
      return 'cadastrado em outra fazenda';
    case 'captured_in_other_farm':
      return 'localizado em outra fazenda';
    case 'outside_expected_pasture':
      return 'fora do pasto esperado';
    case 'outside_pastures':
      return 'fora dos pastos';
    case 'ambiguous_position':
      return 'posição ambígua';
    case 'stale_position':
      return 'posição mais antiga que a última leitura';
    default:
      return alert;
  }
}

export function reasonLabel(reason: string | null): string {
  switch (reason) {
    case null:
      return '—';
    case 'never_read':
      return 'nunca lida';
    case 'awaiting_processing':
      return 'leitura aguardando processamento';
    case 'pose_dropout':
      return 'leitura sem posição do coletor';
    case 'recalculation_blocked':
      return 'sem evidência nova para recalcular';
    case 'no_position_evidence':
      return 'evidência insuficiente para posição';
    default:
      return reason;
  }
}

/* ------------------------------------------------------------------ *
 * M03-R3 — apresentação não técnica
 * ------------------------------------------------------------------ */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Estado simples de um item no instante exibido. `Sem leitura recente` vence:
 * sem leitura nas 24 h anteriores a `asOfMs` (mesma fronteira da faixa
 * [0, 24 h) do servidor). Com posição, ela é `approximate` quando antiga,
 * representativa ou ambígua. Lido recentemente e sem posição fica
 * `unlocated`, o quarto estado aprovado, para não classificá-lo falsamente.
 */
export type SimpleStatus = 'located' | 'approximate' | 'no_recent_read' | 'unlocated';

export function simpleStatus(item: EstimateItem, asOfMs: number): SimpleStatus {
  if (item.lastReadAtMs === null || asOfMs - item.lastReadAtMs >= DAY_MS) return 'no_recent_read';
  if (item.positionState !== 'valid' && item.positionState !== 'last_known') return 'unlocated';
  if (item.positionState === 'last_known' || item.representativeOnly || item.alerts.includes('ambiguous_position')) {
    return 'approximate';
  }
  return 'located';
}

export const SIMPLE_STATUS_LABEL: Record<SimpleStatus, string> = {
  located: 'Localizado',
  approximate: 'Posição aproximada',
  no_recent_read: 'Sem leitura recente',
  unlocated: 'Sem localização',
};

export const ROUND_STATE_LABEL: Record<ReadingRound['state'], string> = {
  completed: 'Concluída',
  processing: 'Em processamento',
  incomplete: 'Incompleta',
};

/**
 * Os quatro números da tela, só das tuplas CONTADAS pelo servidor no instante
 * exibido. Única fonte dos cards, com dado de cache ou de rede: mesmas regras
 * dos indicadores do endpoint completo (localizados = válida + antiga; sem
 * leitura recente = nunca lida ou idade >= 24 h em `asOfMs`).
 */
export function markerIndicators(markers: readonly MapMarker[], asOfMs: number): {
  total: number;
  located: number;
  unlocated: number;
  noRecentRead: number;
} {
  let total = 0;
  let located = 0;
  let noRecentRead = 0;
  for (const marker of markers) {
    if (!marker.counted) continue;
    total += 1;
    if (marker.positionState === 'valid' || marker.positionState === 'last_known') located += 1;
    if (marker.lastReadAtMs === null || asOfMs - marker.lastReadAtMs >= DAY_MS) noRecentRead += 1;
  }
  return { total, located, unlocated: total - located, noRecentRead };
}

const TIME_ZONE = 'America/Sao_Paulo';
const dateTimeFormat = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIME_ZONE, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const timeFormat = new Intl.DateTimeFormat('pt-BR', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });

/** Data/hora no fuso da fazenda, nunca no do navegador. */
export function farmDateTime(ms: number | null): string {
  return ms === null ? '—' : dateTimeFormat.format(ms);
}

export function farmTime(ms: number | null): string {
  return ms === null ? '—' : timeFormat.format(ms);
}

/** `YYYY-MM-DD` → `DD/MM/AAAA`, sem converter em instante. */
export function dayLabel(date: string): string {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}
